/*
 * VARNASTRA LOAD MORE — v2
 * Added for Varnastra collection pages.
 *
 * Purpose:
 * - Loads the next collection page without a full page refresh.
 * - Preserves filters and sorting.
 * - Supports repeated LOAD MORE clicks.
 * - Prevents duplicate requests.
 * - Cancels loading when collection filters/sorting update.
 * - Preserves Prestige product reveal animations.
 */

(function () {
  let loadMoreController = null;

  function cancelLoadMore() {
    if (loadMoreController) {
      loadMoreController.abort();
      loadMoreController = null;
    }
  }

  document.addEventListener("facet:update", cancelLoadMore);

  document.addEventListener("click", async function (event) {
    const loadMoreLink = event.target.closest("[data-load-more]");

    if (!loadMoreLink) {
      return;
    }

    event.preventDefault();

    if (loadMoreLink.getAttribute("aria-disabled") === "true") {
      return;
    }
    /* tracking */
    if (typeof window.gtag === "function") {
      window.gtag("event", "load_more_click");
    }

    const collectionSection = loadMoreLink.closest(".shopify-section");
    const productList = collectionSection?.querySelector(
      '[id^="product-list-"]'
    );

    if (!collectionSection || !productList) {
      return;
    }

    const nextUrl = new URL(loadMoreLink.href, window.location.origin);

    const sectionId = collectionSection.id.replace(
      "shopify-section-",
      ""
    );

    nextUrl.searchParams.set("section_id", sectionId);

    loadMoreController = new AbortController();

    loadMoreLink.setAttribute("aria-disabled", "true");
    loadMoreLink.setAttribute("aria-busy", "true");
    loadMoreLink.textContent = "LOADING…";

    try {
      const response = await fetch(nextUrl.toString(), {
        credentials: "same-origin",
        signal: loadMoreController.signal
      });

      if (!response.ok) {
        throw new Error("Unable to load more products.");
      }

      const html = await response.text();

      const documentFragment = new DOMParser().parseFromString(
        html,
        "text/html"
      );

      const newProductList = documentFragment.querySelector(
        '[id^="product-list-"]'
      );

      if (!newProductList || !loadMoreLink.isConnected) {
        return;
      }

      /*
       * Temporarily remove the reveal-on-scroll attribute
       * from products that are already visible.
       *
       * This prevents Prestige from re-animating the
       * existing products when ProductList.reveal() runs.
       */
      const existingRevealCards = Array.from(
        productList.querySelectorAll(
          'product-card[reveal-on-scroll="true"]'
        )
      );

      existingRevealCards.forEach(function (card) {
        card.removeAttribute("reveal-on-scroll");
      });

      const products = Array.from(newProductList.children);

      products.forEach(function (product) {
        productList.appendChild(
          document.importNode(product, true)
        );
      });

      /*
       * Prestige now sees only the newly appended product
       * cards as reveal-on-scroll cards.
       */
      if (typeof productList.reveal === "function") {
        productList.reveal();
      }

      /*
       * Restore the reveal-on-scroll attribute to the
       * original products for any future theme behavior.
       */
      existingRevealCards.forEach(function (card) {
        card.setAttribute("reveal-on-scroll", "true");
      });

      const newLoadMoreLink = documentFragment.querySelector(
        "[data-load-more]"
      );

      if (newLoadMoreLink) {
        loadMoreLink.href = newLoadMoreLink.href;
        loadMoreLink.removeAttribute("aria-disabled");
        loadMoreLink.removeAttribute("aria-busy");
        loadMoreLink.textContent = "LOAD MORE";
      } else {
        loadMoreLink.closest("[data-load-more-wrapper]")?.remove();
      }
    } catch (error) {
      if (error.name !== "AbortError" && loadMoreLink.isConnected) {
        loadMoreLink.removeAttribute("aria-disabled");
        loadMoreLink.removeAttribute("aria-busy");
        loadMoreLink.textContent = "LOAD MORE";
      }
    } finally {
      loadMoreController = null;
    }
  });
})();